import {useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Clock, Image, KeyRound, Landmark, Link as LinkIcon, MapPin, Pencil, Trash2, Upload, UserCog} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {
    ActionChecklist,
    ExternalLinks,
    ScheduleGrid,
    displayValue,
    mediaUrl,
    yesNo,
} from "../../shared/components/BusinessBlocks";
import {DataState} from "../../shared/components/DataState";
import {ConfirmationDialog} from "../../shared/components/ConfirmationDialog";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {WorkflowRail} from "../../shared/components/WorkflowRail";
import {useTranslation} from "../../shared/i18n";
import {
    deleteBranch,
    deleteBranchGalleryPhoto,
    loadBranch,
    rotateBranchCredential,
    uploadBranchGalleryPhoto,
} from "./api";
import {CredentialRotationDialog} from "./components/CredentialRotationDialog";
import {ReferralSettingsWorkspace} from "./components/ReferralSettingsWorkspace";
import {StaffWorkspace} from "./components/StaffWorkspace";
import {validateGalleryFiles} from "./gallery";
import {invalidateBranchLifecycleQueries} from "./queryInvalidation";
import "./branch.css";

const MANAGER_ROLES = new Set([
    ADMIN_ROLES.SUPERADMIN,
    ADMIN_ROLES.ADMINISTRATOR,
    ADMIN_ROLES.DEPLOYER,
    ADMIN_ROLES.IMPLEMENTATION_HEAD,
]);

const namedReference = (preferredName, legacyValue) => {
    const value = preferredName || (typeof legacyValue === "object" ? legacyValue?.name : legacyValue);
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    return normalized && !/^\d+$/.test(normalized) ? normalized : null;
};

const GalleryManager = ({
    photos = [],
    branchName,
    canManage,
    onUpload,
    onDelete,
    onValidationError,
    uploading,
    deletingId,
    result,
}) => {
    const {t} = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const rows = photos
        .map((item, index) => ({id: item.id, index: index + 1, url: item.photo || item.image || item.file || item.url}))
        .filter((item) => item.url);

    const submit = (event) => {
        event.preventDefault();
        if (selectedFile) {
            onUpload(selectedFile);
            setSelectedFile(null);
            event.currentTarget.reset();
        }
    };

    const selectFile = (event) => {
        const file = event.target.files?.[0] || null;
        const validationError = validateGalleryFiles(file ? [file] : []);
        if (validationError) {
            setSelectedFile(null);
            onValidationError(validationError);
            return;
        }
        onValidationError("");
        setSelectedFile(file);
    };

    return (
        <div className="admin-gallery-manager">
            {canManage ? (
                <form className="admin-upload-strip" onSubmit={submit}>
                    <label className="admin-file-drop">
                        <Upload aria-hidden="true" size={16} />
                        <span>{selectedFile ? selectedFile.name : t("orgs.branchDetail.choosePhoto")}</span>
                        <input
                            aria-label={t("orgs.branchDetail.choosePhotoLabel")}
                            accept="image/*"
                            type="file"
                            onChange={selectFile}
                        />
                    </label>
                    <button
                        aria-label={t("orgs.branchDetail.uploadPhotoLabel")}
                        className="admin-button admin-button-primary"
                        disabled={!selectedFile || uploading}
                        type="submit"
                    >
                        {uploading ? t("orgs.branchDetail.uploading") : t("orgs.branchDetail.upload")}
                    </button>
                </form>
            ) : null}
            {result?.message ? (
                <p className="admin-inline-message" role={result.kind === "error" ? "alert" : "status"}>
                    {result.message}
                </p>
            ) : null}
            {rows.length ? (
                <div className="admin-gallery-grid admin-gallery-editable">
                    {rows.map((item) => (
                        <figure key={`${item.id}-${item.url}`}>
                            <a
                                aria-label={t("orgs.branchDetail.openPhoto", {index: item.index, name: branchName})}
                                href={mediaUrl(item.url)}
                                rel="noreferrer"
                                target="_blank"
                            >
                                <img alt={t("orgs.branchDetail.photoAlt", {index: item.index, name: branchName})} src={mediaUrl(item.url)} />
                            </a>
                            {canManage && item.id ? (
                                <button
                                    aria-label={t("orgs.branchDetail.deletePhoto", {index: item.index, name: branchName})}
                                    disabled={deletingId === item.id}
                                    onClick={() => onDelete(item.id)}
                                    type="button"
                                >
                                    <Trash2 aria-hidden="true" size={14} />
                                </button>
                            ) : null}
                        </figure>
                    ))}
                </div>
            ) : (
                <DataState title={t("orgs.branchDetail.emptyTitle")} copy={t("orgs.branchDetail.emptyCopy")} />
            )}
        </div>
    );
};

export const BranchDetailPage = () => {
    const {t} = useTranslation();
    const {employer_id: placeId, branch_id: branchId} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [galleryResult, setGalleryResult] = useState(null);
    const [deletingGalleryId, setDeletingGalleryId] = useState(null);
    const [galleryDeleteId, setGalleryDeleteId] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const branchQuery = useQuery({
        queryKey: ["admin-branch", branchId],
        queryFn: () => loadBranch(branchId),
        enabled: Boolean(branchId),
    });
    const branch = branchQuery.data || {};
    const staff = branch.personal_set || branch.personal || [];
    const photos = branch.photo_gallery_set || branch.photos || [];
    const externalLinks = [
        {label: "Instagram", url: branch.insta_link},
        {label: "WhatsApp", url: branch.wp_link},
        {label: "2GIS", url: branch.gis_link},
    ];
    const role = capabilities.user?.role;
    const isOwner = role === ADMIN_ROLES.OWNER;
    const hasManageAction = capabilities.hasAction("organization.manage");
    const canManageReferrals = capabilities.hasAction("referral.manage");
    const canEdit = branchQuery.isSuccess && Boolean(branch.name) && hasManageAction && (isOwner || MANAGER_ROLES.has(role));
    const canManageLifecycle = canEdit;

    const deleteBranchMutation = useMutation({
        mutationFn: () => deleteBranch(placeId, branchId),
        onSuccess: () => {
            invalidateBranchLifecycleQueries(queryClient, {placeId, branchId});
            setDialog(null);
            navigate(isOwner ? "/employers" : `/employers/${placeId}`);
        },
        onError: (error) => setDialogError(mapApiError(error)),
    });

    const credentialMutation = useMutation({
        mutationFn: (password) => rotateBranchCredential(branchId, password),
        onSuccess: (response) => {
            invalidateBranchLifecycleQueries(queryClient, {placeId, branchId});
            setDialog(null);
            setDialogError(null);
            setMutationResult({kind: "success", message: response?.message || t("orgs.common.credentialsUpdated")});
        },
        onError: (error) => setDialogError(mapApiError(error)),
    });
    const uploadMutation = useMutation({
        mutationFn: (photo) => uploadBranchGalleryPhoto({branchId, photo}),
        onSuccess: () => {
            setGalleryResult({kind: "success", message: t("orgs.common.photoSaved")});
            invalidateBranchLifecycleQueries(queryClient, {placeId, branchId});
        },
        onError: (error) => {
            setGalleryResult({kind: "error", message: mapApiError(error).message});
        },
    });
    const deleteMutation = useMutation({
        mutationFn: (galleryId) => deleteBranchGalleryPhoto({branchId, galleryId}),
        onMutate: (galleryId) => setDeletingGalleryId(galleryId),
        onSuccess: () => {
            setGalleryResult({kind: "success", message: t("orgs.branchDetail.photoDeleted")});
            invalidateBranchLifecycleQueries(queryClient, {placeId, branchId});
            setDialog(null);
            setDialogError(null);
            setGalleryDeleteId(null);
        },
        onError: (error) => setDialogError(mapApiError(error)),
        onSettled: () => setDeletingGalleryId(null),
    });
    const readinessItems = useMemo(
        () => [
            {label: t("orgs.branchDetail.profile"), ok: Boolean(branch.name && branch.address && branch.description)},
            {label: t("orgs.branchDetail.requisites"), ok: Boolean(branch.bin_number && branch.account_number && branch.off_percent)},
            {label: t("orgs.branchDetail.schedule"), ok: Boolean(branch.monday_start || branch.monday_end)},
            {label: t("orgs.branchDetail.geo"), ok: Boolean(branch.lat && branch.lng)},
            {label: t("orgs.branchDetail.gallery"), ok: photos.length > 0},
            {label: t("orgs.common.staff"), ok: staff.length > 0},
        ],
        [
            branch.account_number,
            branch.address,
            branch.bin_number,
            branch.description,
            branch.lat,
            branch.lng,
            branch.monday_end,
            branch.monday_start,
            branch.name,
            branch.off_percent,
            photos.length,
            staff.length,
            t,
        ],
    );
    const readinessDone = readinessItems.filter((item) => item.ok).length;
    const readinessPercent = Math.round((readinessDone / readinessItems.length) * 100);

    const openDialog = (nextDialog) => {
        setMutationResult(null);
        setDialogError(null);
        setDialog(nextDialog);
    };

    const closeDialog = () => {
        if (deleteBranchMutation.isPending || credentialMutation.isPending || deleteMutation.isPending) return;
        setDialog(null);
        setDialogError(null);
        setGalleryDeleteId(null);
    };

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{branch.name || t("orgs.common.branchNumber", {id: branchId})}</h1>
                    <p className="admin-page-copy">
                        {t("orgs.branchDetail.copy")}
                    </p>
                </div>
                {canEdit ? (
                    <div className="branch-header-actions">
                        <Link className="admin-button admin-button-secondary" to={`/employers/${placeId}/branch/${branchId}/edit`}>
                            <Pencil aria-hidden="true" size={15} /> {isOwner ? t("orgs.branchDetail.requestChange") : t("orgs.common.edit")}
                        </Link>
                        {canManageLifecycle ? (
                            <>
                                <button className="admin-button admin-button-secondary" type="button" onClick={() => openDialog("credential")}>
                                    <KeyRound aria-hidden="true" size={15} /> {t("orgs.branchDetail.changePassword")}
                                </button>
                                <button className="admin-button organization-danger-button" type="button" onClick={() => openDialog("delete")}>
                                    <Trash2 aria-hidden="true" size={15} /> {t("orgs.branchDetail.delete")}
                                </button>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </header>

            <MutationResultBanner result={mutationResult} />

            {branchQuery.isLoading ? (
                <DataState title={t("orgs.branchEditor.loadingTitle")} copy={t("orgs.branchDetail.loadingCopy")} />
            ) : branchQuery.isError ? (
                <DataState title={t("orgs.branchEditor.errorTitle")} copy={t("orgs.branchDetail.errorCopy")} />
            ) : (
                <div className="admin-workflow">
                    <WorkflowRail
                        steps={[
                            {title: t("orgs.branchDetail.stepLocation"), copy: t("orgs.branchDetail.stepLocationCopy")},
                            {title: t("orgs.branchDetail.schedule"), copy: t("orgs.branchDetail.stepScheduleCopy")},
                            {title: t("orgs.common.staff"), copy: t("orgs.branchDetail.stepStaffCopy")},
                            {title: t("orgs.branchDetail.stepQr"), copy: t("orgs.branchDetail.stepQrCopy")},
                        ]}
                    />
                    <section className="admin-page">
                        <section className="admin-grid cols-4">
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.branchDetail.readiness")}</p>
                                <p className="admin-card-value">{readinessPercent}%</p>
                                <p className="admin-card-note">{t("orgs.branchDetail.readinessNote")}</p>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.branchDetail.showcase")}</p>
                                <p className="admin-card-value">{branch.is_shown ? t("orgs.branchDetail.on") : t("orgs.branchDetail.off")}</p>
                                <StatusBadge tone={branch.is_shown ? "success" : "warning"}>
                                    {branch.is_shown ? t("orgs.branchDetail.shown") : t("orgs.branchDetail.hidden")}
                                </StatusBadge>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.common.staff")}</p>
                                <p className="admin-card-value">{staff.length}</p>
                                <p className="admin-card-note">{t("orgs.branchDetail.windowNote", {value: displayValue(branch.staff_attribution_window_minutes, 0)})}</p>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.branchDetail.photos")}</p>
                                <p className="admin-card-value">{photos.length}</p>
                                <p className="admin-card-note">{t("orgs.branchDetail.galleryNote")}</p>
                            </article>
                        </section>

                        <section className="admin-grid cols-2">
                            <article className="admin-card admin-health-card">
                                <p className="admin-card-title">{t("orgs.branchDetail.checklist")}</p>
                                <div className="admin-progress-track">
                                    <span style={{width: `${readinessPercent}%`}} />
                                </div>
                                <div className="admin-readiness-list">
                                    {readinessItems.map((item) => (
                                        <span className={item.ok ? "is-ready" : ""} key={item.label}>
                                            {item.label}
                                        </span>
                                    ))}
                                </div>
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title">{t("orgs.branchDetail.beforePublish")}</p>
                                <ActionChecklist
                                    items={[
                                        t("orgs.branchDetail.checkPhotos"),
                                        t("orgs.branchDetail.checkSchedule"),
                                        t("orgs.branchDetail.checkStaff"),
                                    ]}
                                />
                            </article>
                        </section>

                        <article className="admin-card">
                            <p className="admin-card-title"><MapPin size={14} /> {t("orgs.branchDetail.profileTitle")}</p>
                            <DetailGrid
                                items={[
                                    {label: t("orgs.branchDetail.address"), value: displayValue(branch.address)},
                                    {label: t("orgs.common.phone"), value: displayValue(branch.phone_number)},
                                    {label: t("orgs.common.city"), value: displayValue(namedReference(branch.city_name, branch.city))},
                                    {label: t("orgs.common.category"), value: displayValue(namedReference(branch.category_name, branch.category))},
                                    {label: t("orgs.branchDetail.coords"), value: [branch.lat, branch.lng].filter(Boolean).join(", ") || "—"},
                                    {label: t("orgs.branchDetail.shownLabel"), value: yesNo(branch.is_shown)},
                                ]}
                            />
                        </article>

                        <section className="admin-grid cols-2">
                            <article className="admin-card">
                                <p className="admin-card-title"><Landmark size={14} /> {t("orgs.branchDetail.legalTitle")}</p>
                                <DetailGrid
                                    items={[
                                        {label: t("orgs.branchDetail.admin"), value: displayValue(branch.admin_name)},
                                        {label: t("orgs.branchDetail.bin"), value: displayValue(branch.bin_number)},
                                        {label: t("orgs.common.account"), value: displayValue(branch.account_number)},
                                        {label: t("orgs.common.commission"), value: displayValue(branch.off_percent)},
                                    ]}
                                />
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title"><UserCog size={14} /> {t("orgs.branchDetail.motivationTitle")}</p>
                                <DetailGrid
                                    items={[
                                        {label: t("orgs.branchDetail.enabled"), value: yesNo(branch.personal_motivation_enabled)},
                                        {label: t("orgs.branchDetail.percent"), value: t("orgs.branchDetail.percentValue", {value: displayValue(branch.personal_reward_percent, 0)})},
                                        {label: t("orgs.branchDetail.window"), value: t("orgs.branchDetail.minutes", {value: displayValue(branch.staff_attribution_window_minutes, 0)})},
                                        {label: t("orgs.branchDetail.staffCount"), value: staff.length},
                                    ]}
                                />
                            </article>
                        </section>

                        <article className="admin-card">
                            <p className="admin-card-title"><Clock size={14} /> {t("orgs.branchDetail.scheduleTitle")}</p>
                            <ScheduleGrid entity={branch} />
                        </article>

                        <StaffWorkspace
                            key={`staff-workspace-${branchId}`}
                            branchId={branchId}
                            placeId={placeId}
                            branchName={branch.name}
                            staffDetails={staff}
                            canManage={hasManageAction}
                        />

                        {canManageReferrals ? (
                            <ReferralSettingsWorkspace
                                key={`referral-settings-${branchId}`}
                                branchId={branchId}
                                branchName={branch.name}
                            />
                        ) : null}

                        <section className="admin-grid cols-2">
                            <article className="admin-card">
                                <p className="admin-card-title"><Image size={14} /> {t("orgs.branchDetail.gallery")}</p>
                                <GalleryManager
                                    branchName={branch.name || `#${branchId}`}
                                    canManage={canEdit}
                                    deletingId={deletingGalleryId}
                                    result={galleryResult}
                                    onDelete={(galleryId) => {
                                        setGalleryDeleteId(galleryId);
                                        openDialog("gallery-delete");
                                    }}
                                    onUpload={(photo) => uploadMutation.mutate(photo)}
                                    onValidationError={(message) => setGalleryResult(
                                        message ? {kind: "error", message} : null,
                                    )}
                                    photos={photos}
                                    uploading={uploadMutation.isPending}
                                />
                            </article>
                            <article className="admin-card">
                                <p className="admin-card-title"><LinkIcon size={14} /> {t("orgs.branchDetail.links")}</p>
                                <ExternalLinks links={externalLinks} />
                            </article>
                        </section>
                    </section>
                </div>
            )}
            <ConfirmationDialog
                open={dialog === "delete"}
                title={t("orgs.branchDetail.deleteTitle")}
                description={t("orgs.branchDetail.deleteCopy")}
                entity={branch.name}
                confirmationText={branch.name}
                confirmLabel={t("orgs.branchDetail.deleteConfirm")}
                tone="destructive"
                pending={deleteBranchMutation.isPending}
                error={dialogError}
                dialogKey={`delete-branch-${branchId}`}
                onCancel={closeDialog}
                onConfirm={() => deleteBranchMutation.mutate()}
            />
            <ConfirmationDialog
                open={dialog === "gallery-delete"}
                title={t("orgs.branchDetail.deletePhotoTitle")}
                description={t("orgs.branchDetail.deletePhotoCopy")}
                entity={branch.name}
                confirmLabel={t("orgs.branchDetail.deletePhotoConfirm")}
                tone="destructive"
                pending={deleteMutation.isPending}
                error={dialogError}
                dialogKey={`delete-branch-gallery-${branchId}-${galleryDeleteId}`}
                onCancel={closeDialog}
                onConfirm={() => deleteMutation.mutate(galleryDeleteId)}
            />
            <CredentialRotationDialog
                open={dialog === "credential"}
                entity={branch.name}
                pending={credentialMutation.isPending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={(password) => credentialMutation.mutate(password)}
            />
        </div>
    );
};
